import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { loadPaddle, money } from "@/lib/paddle";
import { trackEvent } from "@/lib/track";

const PRICE = 10;

export default function Subscription() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const navigatingRef = useRef(false);

  const onCheckoutEvent = useCallback(async (ev) => {
    const name = ev?.name || ev?.event;
    if (name === "checkout.completed" && !navigatingRef.current) {
      navigatingRef.current = true;
      toast.success("Payment received — activating your access…");
      for (let i = 0; i < 8; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const u = await refresh();
        if (u?.subscription_status === "active") break;
      }
      setBusy("");
      nav("/subscription/success");
    } else if (name === "checkout.closed") {
      setBusy("");
    }
  }, [refresh, nav]);

  useEffect(() => {
    trackEvent("subscription_view");
    (async () => {
      try {
        const { data } = await api.get("/billing/config");
        setCfg(data);
        await loadPaddle(data.client_token, data.environment, onCheckoutEvent);
        setReady(true);
      } catch (e) {
        setErr(e.response?.data?.detail ? formatApiError(e.response.data.detail) : "Billing isn't available right now. Please try again shortly.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = () => {
    trackEvent("subscribe_click");
    if (!ready || !cfg || !window.Paddle) {
      setErr("Checkout is still loading — please wait a second and try again.");
      return;
    }
    setBusy("sub"); setErr("");
    try {
      window.Paddle.Checkout.open({
        items: [{ priceId: cfg.price_id, quantity: 1 }],
        customer: { email: cfg.email || user?.email },
        customData: { user_id: cfg.user_id },
        settings: { displayMode: "overlay", theme: "dark", allowLogout: false },
      });
    } catch (e) {
      setErr("Could not open the checkout. Please try again.");
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.get("/billing/portal");
      if (data?.url) window.open(data.url, "_blank", "noopener");
      else toast.error("Could not open the billing portal.");
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatApiError(e.response.data.detail) : "Could not open the billing portal.");
    } finally {
      setBusy("");
    }
  };

  const cancelAutoRenew = async () => {
    setBusy("cancel"); setErr("");
    try {
      const { data } = await api.post("/subscription/cancel");
      await refresh();
      toast.success(data?.message || "Auto-renewal cancelled.");
    } catch (e) {
      setErr(e.response?.data?.detail ? formatApiError(e.response.data.detail) : "Could not cancel. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const active = user?.subscription_status === "active";
  const autoRenewOff = user?.subscription_auto_renew === false;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="subscription-page">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Subscription</div>
        <h1 className="text-4xl font-display font-bold tracking-tight text-slate-900 dark:text-white">Go all-in</h1>
        <p className="mt-2 text-slate-600 dark:text-zinc-400">One subscription unlocks every lab and the certificate program. Billed <strong>${PRICE}/month</strong> in USD, auto-renews — cancel anytime. Secure checkout &amp; global tax handled by Paddle.</p>
      </div>

      {err && <Alert variant="destructive" className="mb-6"><AlertDescription data-testid="subscription-error">{err}</AlertDescription></Alert>}

      {active && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="subscription-active-banner">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-display font-bold text-slate-900">Access active</div>
              <div className="text-xs text-slate-600">
                {autoRenewOff
                  ? <>Auto-renew off — access until {user.subscription_current_period_end ? new Date(user.subscription_current_period_end).toLocaleDateString() : "-"}</>
                  : <>Auto-renews {user.subscription_current_period_end ? new Date(user.subscription_current_period_end).toLocaleDateString() : "-"}</>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={busy === "portal"} onClick={openPortal} data-testid="manage-billing-button">
              {busy === "portal" ? "Opening…" : "Manage billing"}
            </Button>
            {!autoRenewOff && (
              <Button variant="outline" size="sm" disabled={busy === "cancel"} onClick={cancelAutoRenew} data-testid="cancel-subscription-button">
                {busy === "cancel" ? "Cancelling…" : "Cancel auto-renewal"}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="max-w-md">
        <div data-testid="plan-card-monthly" className="border-2 border-blue-600 bg-white dark:bg-white/5 p-8 rounded-lg relative">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">ILFAL Pro</div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-5xl font-display font-bold text-slate-900 dark:text-white" data-testid="plan-price-monthly">{money(PRICE, "usd")}</span>
            <span className="text-slate-500">/mo</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">Auto-renews monthly · cancel anytime</div>
          <ul className="mt-6 space-y-2 text-sm text-slate-700 dark:text-zinc-300">
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Every lab unlocked</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Browser + VPN range labs</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Level certificates</li>
            <li className="flex gap-2"><Sparkles className="w-4 h-4 text-blue-600 mt-0.5" /> Cards, PayPal, Apple/Google Pay — taxes included</li>
          </ul>
          <Button
            className="w-full mt-6"
            disabled={busy === "sub" || active || !ready}
            onClick={subscribe}
            data-testid="subscribe-monthly-button"
          >
            {active ? "Subscription active" : busy === "sub" ? "Opening checkout…" : !ready ? "Loading…" : `Subscribe — ${money(PRICE, "usd")}/mo`}
          </Button>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
        <ShieldCheck className="w-4 h-4 text-emerald-600" /> Secure checkout by Paddle (Merchant of Record) — sales tax/VAT handled for you. Auto-renews monthly; cancel anytime and access continues until the period ends.
      </div>
    </div>
  );
}
